import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { PenTool, Type, Calendar, X, Plus, Save, Trash2, Loader2, ChevronLeft, ChevronRight, GripVertical, Layers, Wand2, CheckSquare, AlignLeft, Bold, Italic, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenTool, borderColor: 'border-blue-500', bgColor: 'bg-blue-500/10', textColor: 'text-blue-400' },
  { type: 'name', label: 'Name', icon: Type, borderColor: 'border-emerald-500', bgColor: 'bg-emerald-500/10', textColor: 'text-emerald-400' },
  { type: 'date', label: 'Date', icon: Calendar, borderColor: 'border-amber-500', bgColor: 'bg-amber-500/10', textColor: 'text-amber-400' },
  { type: 'text', label: 'Text', icon: AlignLeft, borderColor: 'border-purple-500', bgColor: 'bg-purple-500/10', textColor: 'text-purple-400' },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, borderColor: 'border-pink-500', bgColor: 'bg-pink-500/10', textColor: 'text-pink-400' },
];

const FONT_FAMILIES = ['Helvetica', 'Times New Roman', 'Courier', 'Arial', 'Georgia'];
const FONT_SIZES = ['8', '10', '11', '12', '14', '16', '18', '20', '24'];
const FONT_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Dark Blue', value: '#1a1a2e' },
  { name: 'Dark Red', value: '#8b0000' },
  { name: 'Navy', value: '#000080' },
  { name: 'Gray', value: '#555555' },
];

const SignatureFieldEditor = ({ open, onOpenChange, documentId, documentContent, documentType, documentName, token, isRegDoc = false, regDocId = null }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState([]);
  const [signingOrder, setSigningOrder] = useState('parallel');
  const [placingType, setPlacingType] = useState(null);
  const [draggingField, setDraggingField] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [resizingField, setResizingField] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);

  // Helper: get the actual rendered page element for accurate position calculations
  const getPageRect = () => {
    if (!contentRef.current) return null;
    const pageEl = contentRef.current.querySelector('.react-pdf__Page');
    if (pageEl) return pageEl.getBoundingClientRect();
    return contentRef.current.getBoundingClientRect();
  };

  const authHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};
  const isPdf = documentType === 'application/pdf';
  const entityId = isRegDoc ? regDocId : documentId;
  const fieldsEndpoint = isRegDoc
    ? `${API_URL}/api/registration-documents/${entityId}/signature-fields`
    : `${API_URL}/api/documents/${entityId}/signature-fields`;

  useEffect(() => {
  if (!open || !entityId) return;
  setLoading(true);
  const fetchData = async () => {
    try {
      const fetches = [
        fetch(fieldsEndpoint, { credentials: 'include', headers: authHeaders() })
      ];
      // handle response...
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  fetchData();
}, [open, entityId, fieldsEndpoint, authHeaders]); // <-- add all used variables

  useEffect(() => {
    if (documentContent && isPdf) {
      const binary = atob(documentContent);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [documentContent, isPdf]);

  const handleAutoDetect = async () => {
    if (!documentId || isRegDoc) return;
    setAutoDetecting(true);
    try {
      const res = await fetch(`${API_URL}/api/documents/${documentId}/auto-detect-fields`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        setFields(data.suggested_fields || []);
        toast.success(`Auto-detected ${data.suggested_fields?.length || 0} fields (${data.document_type_detected} document)`);
      } else { toast.error('Auto-detect failed'); }
    } catch { toast.error('Auto-detect failed'); }
    setAutoDetecting(false);
  };

  const handleDocClick = (e) => {
    if (!placingType || !contentRef.current) {
      if (!placingType) setSelectedField(null);
      return;
    }
    const rect = getPageRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const fieldConfig = FIELD_TYPES.find(f => f.type === placingType);
    const newField = {
      field_id: `fld_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type: placingType,
      label: fieldConfig?.label || placingType,
      position: { x: Math.max(0, Math.min(85, x - 8)), y: Math.max(0, Math.min(97, y - 3)) },
      width: placingType === 'checkbox' ? 8 : 16,
      height: placingType === 'checkbox' ? 3 : 4,
      page: currentPage,
      required: true,
      assigned_to: null,
    };
    if (placingType === 'text') {
      newField.formatting = { font_family: 'Helvetica', font_size: '12', font_color: '#000000', bold: false, italic: false };
      newField.label = 'Text Field';
    }
    if (placingType === 'checkbox') {
      newField.label = 'Checkbox';
      newField.question = '';
    }
    setFields(prev => [...prev, newField]);
    if (window.innerWidth >= 1024) {
      setSelectedField(newField.field_id);
    } else {
      setSelectedField(null);
    }
    setPlacingType(null);
  };

  const handleFieldDragStart = (e, fieldId) => {
    e.preventDefault(); e.stopPropagation();
    setDraggingField(fieldId);
    setSelectedField(fieldId);
    const rect = e.currentTarget.getBoundingClientRect();
    // Support both mouse and touch
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
  };

  const handleDragMove = useCallback((e) => {
    if (!draggingField || !contentRef.current || !containerRef.current) return;
    e.preventDefault();
    const scroll = containerRef.current;
    const content = contentRef.current;
    const scrollRect = scroll.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Auto-scroll when dragging near edges
    const edgeZone = 50;
    if (clientY < scrollRect.top + edgeZone && scroll.scrollTop > 0) {
      if (!scrollIntervalRef.current) {
        scrollIntervalRef.current = setInterval(() => {
          if (containerRef.current) containerRef.current.scrollTop -= 12;
        }, 30);
      }
    } else if (clientY > scrollRect.bottom - edgeZone && scroll.scrollTop < scroll.scrollHeight - scroll.clientHeight) {
      if (!scrollIntervalRef.current) {
        scrollIntervalRef.current = setInterval(() => {
          if (containerRef.current) containerRef.current.scrollTop += 12;
        }, 30);
      }
    } else {
      if (scrollIntervalRef.current) { clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null; }
    }

    const rect = getPageRect();
    if (!rect) return;
    const x = ((clientX - dragOffset.x - rect.left) / rect.width) * 100;
    const y = ((clientY - dragOffset.y - rect.top) / rect.height) * 100;
    setFields(prev => prev.map(f =>
      f.field_id === draggingField ? { ...f, position: { x: Math.max(0, Math.min(85, x)), y: Math.max(0, Math.min(97, y)) } } : f
    ));
  }, [draggingField, dragOffset]);

  const handleDragEnd = useCallback(() => {
    setDraggingField(null);
    if (scrollIntervalRef.current) { clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null; }
  }, []);

  // Resize handlers
  const handleResizeStart = (e, fieldId) => {
    e.preventDefault(); e.stopPropagation();
    const field = fields.find(f => f.field_id === fieldId);
    if (!field) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setResizingField(fieldId);
    setResizeStart({ x: clientX, y: clientY, w: field.width || 16, h: field.height || 4 });
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizingField || !contentRef.current) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const pageEl = contentRef.current.querySelector('.react-pdf__Page');
    const rect = pageEl ? pageEl.getBoundingClientRect() : contentRef.current.getBoundingClientRect();
    const dxPct = ((clientX - resizeStart.x) / rect.width) * 100;
    const dyPct = ((clientY - resizeStart.y) / rect.height) * 100;
    const newW = Math.max(5, Math.min(60, resizeStart.w + dxPct));
    const newH = Math.max(2, Math.min(30, resizeStart.h + dyPct));
    setFields(prev => prev.map(f =>
      f.field_id === resizingField ? { ...f, width: newW, height: newH } : f
    ));
  }, [resizingField, resizeStart]);

  const handleResizeEnd = useCallback(() => { setResizingField(null); }, []);

  useEffect(() => {
    if (resizingField) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      window.addEventListener('touchmove', handleResizeMove, { passive: false });
      window.addEventListener('touchend', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
        window.removeEventListener('touchmove', handleResizeMove);
        window.removeEventListener('touchend', handleResizeEnd);
      };
    }
  }, [resizingField, handleResizeMove, handleResizeEnd]);

  const adjustFieldSize = (fieldId, dw, dh) => {
    setFields(prev => prev.map(f => {
      if (f.field_id !== fieldId) return f;
      return { ...f, width: Math.max(5, Math.min(60, (f.width || 16) + dw)), height: Math.max(2, Math.min(30, (f.height || 4) + dh)) };
    }));
  };


  useEffect(() => {
    if (draggingField) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [draggingField, handleDragMove, handleDragEnd]);

  const removeField = (fieldId) => {
    setFields(prev => prev.filter(f => f.field_id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const assignField = (fieldId, userId) => {
    setFields(prev => prev.map(f => f.field_id === fieldId ? { ...f, assigned_to: userId || null } : f));
  };

  const updateFieldLabel = (fieldId, label) => {
    setFields(prev => prev.map(f => f.field_id === fieldId ? { ...f, label } : f));
  };

  const updateFieldQuestion = (fieldId, question) => {
    setFields(prev => prev.map(f => f.field_id === fieldId ? { ...f, question } : f));
  };

  const updateFieldFormatting = (fieldId, key, value) => {
    setFields(prev => prev.map(f => {
      if (f.field_id !== fieldId) return f;
      return { ...f, formatting: { ...(f.formatting || {}), [key]: value } };
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = JSON.stringify({ fields, signing_order: signingOrder });
      const res = await fetch(fieldsEndpoint, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: payload
      });
      if (res.ok) { toast.success(`Saved ${fields.length} fields`); onOpenChange(false); }
      else {
        let errMsg = `Save failed (${res.status})`;
        try { const err = await res.json(); errMsg = err.detail || errMsg; } catch {}
        if (res.status === 401 || res.status === 403) {
          toast.error('Session expired. Please log out and log back in, then try again.');
        } else {
          toast.error(errMsg);
        }
      }
    } catch (err) {
      console.error('Save fields error:', err);
      // Retry once on network error
      try {
        const retryRes = await fetch(fieldsEndpoint, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ fields, signing_order: signingOrder })
        });
        if (retryRes.ok) { toast.success(`Saved ${fields.length} fields`); onOpenChange(false); setSaving(false); return; }
      } catch {}
      toast.error('Save failed. Please check your connection and try again.');
    }
    setSaving(false);
  };

  const handleClearAll = async () => {
    setFields([]); setSelectedField(null);
    if (!isRegDoc) {
      try {
        await fetch(`${API_URL}/api/documents/${documentId}/signature-fields`, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
      } catch { /* ignore */ }
    }
    toast.success('All fields removed');
  };

  const pageFields = fields.filter(f => f.page === currentPage);
  const selectedFieldData = fields.find(f => f.field_id === selectedField);
  const getUserName = (userId) => { const u = users.find(u => u.user_id === userId); return u ? u.name : null; };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-5xl w-[95vw] max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-3 sm:p-6" data-testid="field-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <PenTool className="w-5 h-5 text-primary" />
            {isRegDoc ? 'Registration Doc Fields' : 'Signature Fields'}: {documentName}
          </DialogTitle>
          <DialogDescription className="text-white/60">Place signature, name, text, date, and checkbox fields on the document.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden mt-2 flex gap-3 flex-col lg:flex-row">
          {/* Document area — always takes full height */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            {/* Toolbar */}
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              {FIELD_TYPES.map(ft => {
                const Icon = ft.icon;
                return (
                  <Button key={ft.type} type="button" size="sm"
                    onClick={() => setPlacingType(placingType === ft.type ? null : ft.type)}
                    className={`h-7 text-xs gap-1 ${placingType === ft.type ? `${ft.bgColor} ${ft.textColor} ${ft.borderColor} border` : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'}`}
                    data-testid={`add-field-${ft.type}`}>
                    <Plus className="w-3 h-3" /><Icon className="w-3 h-3" /><span className="hidden sm:inline">{ft.label}</span>
                  </Button>
                );
              })}
              {!isRegDoc && (
                <Button type="button" size="sm" onClick={handleAutoDetect} disabled={autoDetecting}
                  className="h-7 text-xs bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 gap-1" data-testid="auto-detect-btn">
                  {autoDetecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  <span className="hidden sm:inline">Auto-Detect</span>
                </Button>
              )}
              <div className="ml-auto flex gap-1.5">
                {fields.length > 0 && (
                  <Button type="button" size="sm" variant="outline" onClick={handleClearAll}
                    className="h-7 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="clear-all-fields">
                    <Trash2 className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
                <Button type="button" size="sm" onClick={handleSave} disabled={saving}
                  className="h-7 text-xs bg-primary text-black hover:bg-primary/90" data-testid="save-fields-btn">
                  {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                  Save ({fields.length})
                </Button>
              </div>
            </div>

            {placingType && (
              <p className="text-xs text-center py-1 rounded-sm bg-white/5 text-white/60 mb-2">
                Click on the document to place a <strong className={FIELD_TYPES.find(f => f.type === placingType)?.textColor}>{FIELD_TYPES.find(f => f.type === placingType)?.label}</strong> field
              </p>
            )}

            {/* Page nav & zoom */}
            <div className="flex items-center justify-center gap-2 py-1 mb-1">
              {numPages > 1 && (
                <>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1} className="text-white/80 h-7"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-white/60 text-xs">Page {currentPage} / {numPages}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages} className="text-white/80 h-7"><ChevronRight className="w-4 h-4" /></Button>
                  <span className="text-white/10">|</span>
                </>
              )}
              <div className="flex items-center gap-1">
                <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} disabled={zoom <= 0.5} className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30" data-testid="editor-zoom-out"><ZoomOut className="w-4 h-4" /></button>
                <button onClick={() => setZoom(1)} className="px-1.5 py-0.5 rounded hover:bg-white/10 text-white/50 text-[10px] font-mono min-w-[40px] text-center" data-testid="editor-zoom-reset">{Math.round(zoom * 100)}%</button>
                <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} disabled={zoom >= 3} className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30" data-testid="editor-zoom-in"><ZoomIn className="w-4 h-4" /></button>
              </div>
            </div>

            <div ref={containerRef}
              className={`border border-white/20 rounded-sm bg-white overflow-auto flex-1 min-h-[200px] ${placingType ? 'cursor-crosshair' : ''}`}
              style={{ maxHeight: 'calc(95vh - 220px)' }}
              onClick={handleDocClick}
              onTouchEnd={(e) => {
                if (!placingType || !contentRef.current) return;
                const touch = e.changedTouches?.[0];
                if (!touch) return;
                const pageEl = contentRef.current.querySelector('.react-pdf__Page');
                const rect = pageEl ? pageEl.getBoundingClientRect() : contentRef.current.getBoundingClientRect();
                const x = ((touch.clientX - rect.left) / rect.width) * 100;
                const y = ((touch.clientY - rect.top) / rect.height) * 100;
                const fieldConfig = FIELD_TYPES.find(f => f.type === placingType);
                const newField = {
                  field_id: `fld_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                  type: placingType,
                  label: fieldConfig?.label || placingType,
                  position: { x: Math.max(0, Math.min(85, x - 8)), y: Math.max(0, Math.min(97, y - 3)) },
                  width: placingType === 'checkbox' ? 8 : 16,
                  height: placingType === 'checkbox' ? 3 : 4,
                  page: currentPage,
                  required: true,
                  assigned_to: null,
                };
                if (placingType === 'text') {
                  newField.formatting = { font_family: 'Helvetica', font_size: '12', font_color: '#000000', bold: false, italic: false };
                  newField.label = 'Text Field';
                }
                if (placingType === 'checkbox') {
                  newField.label = 'Checkbox';
                  newField.question = '';
                }
                setFields(prev => [...prev, newField]);
                setSelectedField(null);
                setPlacingType(null);
              }}
              data-testid="field-editor-area">
              <div ref={contentRef} className="relative" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: `${100 / zoom}%` }}>
              {isPdf && pdfUrl ? (
                <Document file={pdfUrl}
                  onLoadSuccess={({ numPages: np }) => { setNumPages(np); setLoading(false); }}
                  loading={<div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>}
                  error={<div className="text-red-500 text-center p-8">Failed to load PDF</div>}>
                  <Page pageNumber={currentPage} renderTextLayer={false} renderAnnotationLayer={false}
                    width={containerRef.current?.getBoundingClientRect().width || 600} />
                </Document>
              ) : documentContent ? (
                documentType?.startsWith('image/') ? (
                  <img src={`data:${documentType};base64,${documentContent}`} alt={documentName} className="w-full h-auto" />
                ) : (
                  <pre className="text-gray-800 text-sm p-4 whitespace-pre-wrap font-mono">{(() => { try { return atob(documentContent); } catch { return 'Preview not available'; } })()}</pre>
                )
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>
              )}

              {/* Field overlays — positioned with percentage-based absolute inside the page-matching area */}
              {pageFields.map(field => {
                const config = FIELD_TYPES.find(f => f.type === field.type);
                const Icon = config?.icon || PenTool;
                const isSelected = selectedField === field.field_id;
                const fw = field.width || (field.type === 'checkbox' ? 8 : 16);
                const fh = field.height || 4;
                return (
                  <div key={field.field_id}
                    className={`absolute z-10 select-none cursor-move touch-none border-2 border-dashed ${config?.borderColor} ${config?.bgColor} rounded overflow-hidden ${isSelected ? 'ring-2 ring-white/50' : ''} ${draggingField === field.field_id ? 'opacity-80 shadow-xl' : ''}`}
                    style={{ left: `${field.position.x}%`, top: `${field.position.y}%`, width: `${fw}%`, height: `${fh}%` }}
                    onMouseDown={(e) => handleFieldDragStart(e, field.field_id)}
                    onTouchStart={(e) => handleFieldDragStart(e, field.field_id)}
                    onClick={(e) => { e.stopPropagation(); setSelectedField(field.field_id); }}
                    data-testid={`field-${field.field_id}`}>
                    <div className="flex items-center gap-1 px-1.5 py-0.5 h-full">
                      <GripVertical className={`w-3 h-3 ${config?.textColor} opacity-50 flex-shrink-0`} />
                      <Icon className={`w-3.5 h-3.5 ${config?.textColor} flex-shrink-0`} />
                      <span className={`text-[10px] font-medium ${config?.textColor} whitespace-nowrap truncate`}>{field.label}</span>
                      <button onClick={(e) => { e.stopPropagation(); removeField(field.field_id); }}
                        className="ml-auto text-white/40 hover:text-red-400 flex-shrink-0" data-testid={`remove-field-${field.field_id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    {/* Resize handle */}
                    {isSelected && (
                      <div
                        className={`absolute bottom-0 right-0 w-3 h-3 cursor-se-resize touch-none ${config?.bgColor} border-t border-l ${config?.borderColor} rounded-tl`}
                        onMouseDown={(e) => handleResizeStart(e, field.field_id)}
                        onTouchStart={(e) => handleResizeStart(e, field.field_id)}
                        data-testid={`resize-${field.field_id}`}
                      >
                        <svg viewBox="0 0 12 12" className={`w-full h-full ${config?.textColor}`}>
                          <path d="M11 1L1 11M11 5L5 11M11 9L9 11" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
              {/* Spacer for mobile scrolling — outside contentRef so it doesn't affect field position calculations */}
              <div className="h-[120px] lg:h-0" aria-hidden="true" />
            </div>
          </div>

          {/* Right: Properties panel — DESKTOP ONLY (lg+) */}
          <div className="hidden lg:block w-56 flex-shrink-0 space-y-2 overflow-y-auto">
            {selectedFieldData ? (
              <div className="bg-white/5 rounded-sm p-2.5 border border-white/10 space-y-2">
                <p className="text-white/60 text-xs font-medium">Field Properties</p>
                <div>
                  <label className="text-white/40 text-[10px]">Label</label>
                  <Input value={selectedFieldData.label} onChange={(e) => updateFieldLabel(selectedField, e.target.value)}
                    className="bg-black/50 border-white/10 text-white h-7 text-xs" data-testid="field-label-input" />
                </div>
                {selectedFieldData.type === 'checkbox' && (
                  <div>
                    <label className="text-white/40 text-[10px]">Question <span className="text-white/20">(not shown on PDF)</span></label>
                    <Input value={selectedFieldData.question || ''} onChange={(e) => updateFieldQuestion(selectedField, e.target.value)}
                      placeholder="e.g. Do you agree to the terms?"
                      className="bg-black/50 border-white/10 text-white h-7 text-xs" data-testid="field-question-input" />
                  </div>
                )}
                <div className="space-y-1.5 border-t border-white/10 pt-2">
                  <p className="text-white/50 text-[10px] font-medium">Size</p>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-white/40 text-[10px]">Width</label>
                      <div className="flex items-center gap-1">
                        <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, -2, 0)} className="h-6 w-6 p-0 border-white/10 text-white/60 hover:bg-white/10" data-testid="field-width-minus">-</Button>
                        <span className="text-white/70 text-[10px] w-8 text-center">{Math.round(selectedFieldData.width || 16)}%</span>
                        <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, 2, 0)} className="h-6 w-6 p-0 border-white/10 text-white/60 hover:bg-white/10" data-testid="field-width-plus">+</Button>
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-white/40 text-[10px]">Height</label>
                      <div className="flex items-center gap-1">
                        <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, 0, -1)} className="h-6 w-6 p-0 border-white/10 text-white/60 hover:bg-white/10" data-testid="field-height-minus">-</Button>
                        <span className="text-white/70 text-[10px] w-8 text-center">{Math.round(selectedFieldData.height || 4)}%</span>
                        <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, 0, 1)} className="h-6 w-6 p-0 border-white/10 text-white/60 hover:bg-white/10" data-testid="field-height-plus">+</Button>
                      </div>
                    </div>
                  </div>
                </div>
                {selectedFieldData.type === 'text' && (
                  <div className="space-y-2 border-t border-white/10 pt-2">
                    <p className="text-white/50 text-[10px] font-medium">Formatting</p>
                    <div><label className="text-white/40 text-[10px]">Font</label>
                      <Select value={selectedFieldData.formatting?.font_family || 'Helvetica'} onValueChange={(v) => updateFieldFormatting(selectedField, 'font_family', v)}>
                        <SelectTrigger className="bg-black/50 border-white/10 text-white h-7 text-xs" data-testid="text-font-family"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">{FONT_FAMILIES.map(f => (<SelectItem key={f} value={f} className="text-white text-xs">{f}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1"><label className="text-white/40 text-[10px]">Size</label>
                        <Select value={selectedFieldData.formatting?.font_size || '12'} onValueChange={(v) => updateFieldFormatting(selectedField, 'font_size', v)}>
                          <SelectTrigger className="bg-black/50 border-white/10 text-white h-7 text-xs" data-testid="text-font-size"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border-white/10">{FONT_SIZES.map(s => (<SelectItem key={s} value={s} className="text-white text-xs">{s}px</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1"><label className="text-white/40 text-[10px]">Color</label>
                        <Select value={selectedFieldData.formatting?.font_color || '#000000'} onValueChange={(v) => updateFieldFormatting(selectedField, 'font_color', v)}>
                          <SelectTrigger className="bg-black/50 border-white/10 text-white h-7 text-xs" data-testid="text-font-color"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border-white/10">{FONT_COLORS.map(c => (<SelectItem key={c.value} value={c.value} className="text-white text-xs"><span className="inline-block w-3 h-3 rounded-full mr-1" style={{ background: c.value }} />{c.name}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={() => updateFieldFormatting(selectedField, 'bold', !selectedFieldData.formatting?.bold)}
                        className={`h-7 text-xs flex-1 ${selectedFieldData.formatting?.bold ? 'bg-purple-600 text-white' : 'bg-white/5 text-white/70'}`} data-testid="text-bold-toggle"><Bold className="w-3 h-3 mr-1" /> Bold</Button>
                      <Button type="button" size="sm" onClick={() => updateFieldFormatting(selectedField, 'italic', !selectedFieldData.formatting?.italic)}
                        className={`h-7 text-xs flex-1 ${selectedFieldData.formatting?.italic ? 'bg-purple-600 text-white' : 'bg-white/5 text-white/70'}`} data-testid="text-italic-toggle"><Italic className="w-3 h-3 mr-1" /> Italic</Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white/5 rounded-sm p-3 border border-white/10 text-center"><p className="text-white/30 text-xs">Click a field to edit properties</p></div>
            )}
            <div className="bg-white/5 rounded-sm p-2.5 border border-white/10">
              <p className="text-white/60 text-xs font-medium flex items-center gap-1 mb-1.5"><Layers className="w-3.5 h-3.5" /> Fields ({fields.length})</p>
              {fields.length === 0 ? (<p className="text-white/30 text-[10px]">No fields added yet</p>) : (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {fields.map(f => { const cfg = FIELD_TYPES.find(ft => ft.type === f.type); const Ic = cfg?.icon || PenTool; return (
                    <button key={f.field_id} onClick={() => setSelectedField(f.field_id)}
                      className={`w-full text-left flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs transition-colors ${selectedField === f.field_id ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                      <Ic className={`w-3 h-3 ${cfg?.textColor} flex-shrink-0`} /><span className="text-white/70 truncate">{f.label}</span>
                    </button>); })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MOBILE bottom sheet — fixed overlay, doesn't push document away */}
        {selectedFieldData && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[60] bg-[#0A0A0A] border-t border-white/20 p-3 rounded-t-lg shadow-2xl" data-testid="mobile-field-props">
            <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-2" />
            <div className="flex items-center justify-between mb-2">
              <p className="text-white text-sm font-semibold truncate flex-1">{selectedFieldData.label}</p>
              <button onClick={() => setSelectedField(null)} className="text-white/50 hover:text-white p-1 ml-2" data-testid="close-mobile-props"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <label className="text-white/40 text-[10px]">Label</label>
                <Input value={selectedFieldData.label} onChange={(e) => updateFieldLabel(selectedField, e.target.value)}
                  className="bg-black/50 border-white/10 text-white h-8 text-xs" />
              </div>
              <div className="flex gap-1 items-center">
                <span className="text-white/40 text-[10px]">W</span>
                <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, -2, 0)} className="h-7 w-7 p-0 border-white/10 text-white/60">-</Button>
                <span className="text-white/70 text-xs w-7 text-center">{Math.round(selectedFieldData.width || 16)}</span>
                <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, 2, 0)} className="h-7 w-7 p-0 border-white/10 text-white/60">+</Button>
                <span className="text-white/40 text-[10px] ml-1">H</span>
                <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, 0, -1)} className="h-7 w-7 p-0 border-white/10 text-white/60">-</Button>
                <span className="text-white/70 text-xs w-7 text-center">{Math.round(selectedFieldData.height || 4)}</span>
                <Button type="button" size="sm" variant="outline" onClick={() => adjustFieldSize(selectedField, 0, 1)} className="h-7 w-7 p-0 border-white/10 text-white/60">+</Button>
              </div>
            </div>
            {selectedFieldData.type === 'text' && (
              <div className="flex gap-2 items-center mt-2">
                <Select value={selectedFieldData.formatting?.font_size || '12'} onValueChange={(v) => updateFieldFormatting(selectedField, 'font_size', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white h-7 text-xs w-20"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">{FONT_SIZES.map(s => (<SelectItem key={s} value={s} className="text-white text-xs">{s}px</SelectItem>))}</SelectContent>
                </Select>
                <Button type="button" size="sm" onClick={() => updateFieldFormatting(selectedField, 'bold', !selectedFieldData.formatting?.bold)}
                  className={`h-7 px-2 text-xs ${selectedFieldData.formatting?.bold ? 'bg-purple-600 text-white' : 'bg-white/5 text-white/70'}`}><Bold className="w-3 h-3" /></Button>
                <Button type="button" size="sm" onClick={() => updateFieldFormatting(selectedField, 'italic', !selectedFieldData.formatting?.italic)}
                  className={`h-7 px-2 text-xs ${selectedFieldData.formatting?.italic ? 'bg-purple-600 text-white' : 'bg-white/5 text-white/70'}`}><Italic className="w-3 h-3" /></Button>
              </div>
            )}
            {selectedFieldData.type === 'checkbox' && (
              <div className="mt-2">
                <label className="text-white/40 text-[10px]">Question <span className="text-white/20">(not on PDF)</span></label>
                <Input value={selectedFieldData.question || ''} onChange={(e) => updateFieldQuestion(selectedField, e.target.value)}
                  placeholder="e.g. Do you agree?" className="bg-black/50 border-white/10 text-white h-8 text-xs" />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SignatureFieldEditor;
